<template>
  <div class="checklist-container">
    <h1 class="page-title">Checklist Items</h1>

    <!-- 【原样保留你最初的筛选搜索栏，完全没改动】 -->
    <div class="filter-section">
      <div class="filter-grid">
        <div class="filter-box">
          <span class="filter-label-inline">ID</span>
          <input
            v-model="filters.id"
            type="text"
            class="filter-input-inline"
            placeholder="Search.."
            maxlength="50"
            @keyup.enter="handleSearch"
          />
        </div>

        <div
          class="filter-box"
          :class="{ 'is-open': dropdownOpen.type }"
          @click.stop="toggleDropdown('type')"
        >
          <span class="filter-label-inline">Type:</span>
          <input
            type="text"
            class="filter-select-inline"
            :value="getTypeDisplayText()"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="arrow" class="select-arrow-icon" />
          <div v-if="dropdownOpen.type" class="dropdown-panel" @click.stop>
            <div
              v-for="option in typeOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.type === option.value }"
              @click="selectOption('type', option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div
          class="filter-box"
          :class="{ 'is-open': dropdownOpen.section }"
          @click.stop="toggleDropdown('section')"
        >
          <span class="filter-label-inline">Section:</span>
          <input
            type="text"
            class="filter-select-inline"
            :value="getSectionDisplayText()"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="arrow" class="select-arrow-icon" />
          <div v-if="dropdownOpen.section" class="dropdown-panel" @click.stop>
            <div
              v-for="option in sectionOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.section === option.value }"
              @click="selectOption('section', option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div
          class="filter-box"
          :class="{ 'is-open': dropdownOpen.followUp }"
          @click.stop="toggleDropdown('followUp')"
        >
          <span class="filter-label-inline">Follow-up:</span>
          <input
            type="text"
            class="filter-select-inline"
            :value="getFollowUpDisplayText()"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="arrow" class="select-arrow-icon" />
          <div v-if="dropdownOpen.followUp" class="dropdown-panel" @click.stop>
            <div
                v-for="option in followUpOptions"
                :key="option.value"
                class="dropdown-option"
                :class="{ active: selectedFollowUp.includes(option.value) }"
                @click="toggleFollowUp(option.value)"
              >
                <div class="checkbox" :class="{ checked: selectedFollowUp.includes(option.value) }">
                  <svg v-if="selectedFollowUp.includes(option.value)" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 6L5 9L10 3"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <span>{{ option.label }}</span>
              </div>
          </div>
        </div>

        <div
          class="filter-box"
          :class="{ 'is-open': dropdownOpen.duration }"
          @click.stop="toggleDropdown('duration')"
        >
          <span class="filter-label-inline">Duration:</span>
          <input
            type="text"
            class="filter-select-inline"
            :value="getDurationDisplayText()"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="arrow" class="select-arrow-icon" />
          <div v-if="dropdownOpen.duration" class="dropdown-panel" @click.stop>
            <div
              v-for="option in durationOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.duration === option.value }"
              @click="selectOption('duration', option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div
          class="filter-box"
          :class="{ 'is-open': dropdownOpen.seasons }"
          @click.stop="toggleDropdown('seasons')"
        >
          <span class="filter-label-inline">Seasons:</span>
          <input
            type="text"
            class="filter-select-inline"
            :value="getSeasonsDisplayText()"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="arrow" class="select-arrow-icon" />
          <div v-if="dropdownOpen.seasons" class="dropdown-panel" @click.stop>
            <div
              v-for="option in seasonsOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: selectedSeasons.includes(option.value) }"
              @click="toggleSeasons(option.value)"
            >
              <div class="checkbox" :class="{ checked: selectedSeasons.includes(option.value) }">
                <svg v-if="selectedSeasons.includes(option.value)" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div class="filter-actions-inline">
          <button class="btn-search" @click="handleSearch">Search</button>
          <button class="btn-clear" @click="handleClear">Clear</button>
        </div>
      </div>
    </div>

    <!-- 下方list-header、列表、空状态改成技师页面样式，筛选不动 -->
    <div class="action-bar">
      <span class="count-info" :class="{ 'count-info--pending': loading }">{{ totalItems }} Items</span>
      <button class="btn btn-new" @click="handleNew">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
        NEW
      </button>
    </div>

    <div class="list-body" :aria-busy="loading">
      <LoadingIndicator v-if="loading" />

      <div v-else-if="isInitialEmptyPhase || checklistItems.length === 0" class="orders-empty">
        <img :src="imgListBlank" class="orders-empty-img" alt="" />
        <p class="orders-empty-msg">No items found</p>
      </div>

      <div v-else class="list-container">
      <div v-for="item in checklistItems" :key="item.id" class="list-card">
        <div class="card-header">
          <span class="header-id">ID:</span>
          <span class="header-id-value">{{ item.itemId }}</span>
        </div>
        <div class="card-body">
          <div class="column-main">
            <div class="item-icon">
              <img v-if="item.icon" :src="getIconUrl(item.icon)" :alt="item.title" />
              <img v-else :src="downarrowIcon" alt="placeholder" class="placeholder-icon" />
            </div>
            <div class="item-info">
              <div class="item-title">{{ item.title }}</div>
              <div class="item-duration">{{ item.duration }}</div>
              <div class="item-tags">
                <span v-if="item.isRoutine" class="tag tag-routine">Routine</span>
                <span v-if="item.isBaseline" class="tag tag-baseline">Baseline</span>
              </div>
              <div v-if="item.seasons && item.seasons.length" class="item-seasons">
                <span v-for="season in item.seasons" :key="season" class="season-tag">{{
                  season
                }}</span>
              </div>
            </div>
          </div>
          <div class="column-section">{{ item.section }}</div>
          <div class="column-status">
            <span :class="['status-badge', item.statusText === 'Active' ? 'active' : 'inactive']">
              {{ item.statusText }}
            </span>
            <button class="view-details" @click="handleViewDetails(item)">View Details</button>
          </div>
          <div class="column-actions">
            <button class="action-btn" @click.stop="handleEdit(item)">
              <img :src="editIcon" alt="edit" class="action-icon" />Edit
            </button>
            <button class="action-btn" @click.stop="handleSetValue(item)">
              <img :src="setValueIcon" alt="set value" class="action-icon" />Set Value
            </button>
            <button
              v-if="item.statusText === 'Active'"
              class="action-btn"
              @click.stop="handleToggleStatus(item)"
            >
              <img :src="deactivateIcon" alt="deactivate" class="action-icon" />Deactivate
            </button>
            <button v-else class="action-btn" @click.stop="handleToggleStatus(item)">
              <img :src="activateIcon" alt="activate" class="action-icon" />Reactivate
            </button>
            <button class="action-btn action-delete" @click.stop="handleDelete(item)">
              <img :src="deleteIcon" alt="delete" class="action-icon" />Delete
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- 底部固定分页 -->
    <div class="pagination-wrapper">
      <PageComponent
        :current-page="currentPage"
        :total-pages="Math.max(1, totalPages)"
        @page-change="handlePageChange"
      />
    </div>

    <NewItem v-if="showNewModal" @close="showNewModal = false" @saved="handleNewSaved" @toast="handleToast" />

    <!-- Toast组件 -->
    <div class="toast-area">
      <ToastComponent :visible="toastVisible" :type="toastType" :message="toastMessage" />
    </div>
    <EditItem
      v-if="showEditModal && currentItem"
      :item-id="currentItem.id"
      @close="handleEditClose"
      @saved="handleEditSaved"
      @toast="handleToast"
    />
    <SetValue
      v-if="showSetValueModal && currentItem"
      :item-info="{
        id: currentItem.id,
        title: currentItem.title,
        type: currentItem.isRoutine ? 'Routine' : 'Baseline',
        values: currentItem.values,
      }"
      @close="handleSetValueClose"
      @saved="handleSetValueSaved"
      @toast="handleToast"
    />
    <CheckActive
      v-if="showStatusModal && currentItem"
      :item-id="currentItem.id"
      :action="statusAction"
      :item-name="currentItem.title"
      @close="handleStatusClose"
      @saved="handleStatusSaved"
      @toast="handleToast"
    />
    <DeleteCheck
      v-if="showDeleteModal && currentItem"
      :item-id="currentItem.id"
      :item-name="currentItem.title"
      @close="handleDeleteClose"
      @deleted="handleDeleteConfirmed"
      @toast="handleToast"
    />
    <ItemDetail
      v-if="showDetailModal"
      :item-id="currentItem?.id || ''"
      @close="showDetailModal = false"
      @updated="handleDetailUpdated"
      @deleted="handleDetailDeleted"
      @toast="handleToast"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onActivated, onUnmounted } from 'vue'
import editIcon from '@/assets/edit.png'
import deleteIcon from '@/assets/delete(1).png'
import imgListBlank from '@/assets/list_blank@2x.png'
import setValueIcon from '@/assets/edit(3).png'
import downarrowIcon from '@/assets/downarrow.png'
import activateIcon from '@/assets/activate.png'
import deactivateIcon from '@/assets/Schedule.png'
import { getChecklistList, parseChecklistResponse } from '@/api/checklist'
import { getChecklistDetail } from '@/api/itemdetail'
import type { ChecklistItem as BackendChecklistItem, ChecklistListParams } from '@/api/checklist'
import { formatSeasonDisplayLabels } from '@/utils/seasons'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import NewItem from './NewItem.vue'
import EditItem from './EditItem.vue'
import ToastComponent from '@/components/ToastComponent.vue'
import SetValue from './SetValue.vue'
import CheckActive from './CheckActive.vue'
import DeleteCheck from './DeleteCheck.vue'
import ItemDetail from './ItemDetail.vue'
import '@/styles/checklist-item-tags.css'
import '@/styles/care-card-title.css'

interface ChecklistItem {
  id: string
  /** 业务编号，列表「ID:」展示用（后端 item_id，如 CH0001） */
  itemId: string
  icon: string
  title: string
  duration: string
  isRoutine: boolean
  isBaseline: boolean
  seasons: string[]
  section: string
  price: string
  statusText: string
  values?: {
    condition: string[]
    location: string[]
    task: string[]
    time: string[]
  }
}

interface FilterOption {
  value: string
  label: string
}

interface DropdownState {
  type: boolean
  section: boolean
  followUp: boolean
  duration: boolean
  seasons: boolean
}

const filters = reactive({
  id: '',
  type: 'all',
  section: 'all',
  followUp: '',
  duration: 'all',
  seasons: '',
})

const selectedFollowUp = ref<string[]>([])
const selectedSeasons = ref<string[]>([])
const currentPage = ref(1)
const totalPages = ref(1)
const totalItems = ref(0)
const loading = ref(false)
const listLoaded = ref(false)
const isInitialEmptyPhase = ref(true)
const INITIAL_EMPTY_MIN_MS = 300
let initialFetchTimer: ReturnType<typeof setTimeout> | null = null
const showNewModal = ref(false)
const showEditModal = ref(false)
const showSetValueModal = ref(false)
const showStatusModal = ref(false)
const showDeleteModal = ref(false)

// Toast相关状态
const toastVisible = ref(false)
const toastType = ref<'success' | 'warning' | 'error'>('success')
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null

function handleToast(data: { type: 'success' | 'warning' | 'error'; message: string }): void {
  if (toastTimer) {
    clearTimeout(toastTimer)
  }
  toastType.value = data.type
  toastMessage.value = data.message
  toastVisible.value = true
  toastTimer = setTimeout(() => {
    toastVisible.value = false
    toastTimer = null
  }, 2000)
}
const showDetailModal = ref(false)
const currentItem = ref<ChecklistItem | null>(null)
const statusAction = ref<'deactivate' | 'reactivate'>('deactivate')
const checklistItems = ref<ChecklistItem[]>([])

const typeOptions = ref<FilterOption[]>([])
const sectionOptions = ref<FilterOption[]>([])
const followUpOptions = ref<FilterOption[]>([])
const durationOptions = ref<FilterOption[]>([])
const seasonsOptions = ref<FilterOption[]>([])

const dropdownOpen = reactive<DropdownState>({
  type: false,
  section: false,
  followUp: false,
  duration: false,
  seasons: false,
})

function getTypeDisplayText(): string {
  if (filters.type === 'all') return ''
  const option = typeOptions.value.find((o) => o.value === filters.type)
  return option?.label || ''
}

function getSectionDisplayText(): string {
  if (filters.section === 'all') return ''
  const option = sectionOptions.value.find((o) => o.value === filters.section)
  return option?.label || ''
}

function getFollowUpDisplayText(): string {
  if (selectedFollowUp.value.length === 0) return ''
  if (selectedFollowUp.value.includes('all')) return 'All'
  const selectedLabels = selectedFollowUp.value
    .map((value) => {
      const option = followUpOptions.value.find((o) => o.value === value)
      return option?.label || ''
    })
    .filter(Boolean)
  return selectedLabels.join(', ')
}

function toggleFollowUp(value: string): void {
  if (value === 'all') {
    if (selectedFollowUp.value.includes('all')) {
      selectedFollowUp.value = []
    } else {
      // 选择All时，同时选中所有其他选项
      selectedFollowUp.value = followUpOptions.value.map((o) => o.value)
    }
  } else {
    // 取消All的选中状态
    selectedFollowUp.value = selectedFollowUp.value.filter((v) => v !== 'all')
    const index = selectedFollowUp.value.indexOf(value)
    if (index > -1) {
      selectedFollowUp.value.splice(index, 1)
    } else {
      selectedFollowUp.value.push(value)
    }
  }
  filters.followUp = selectedFollowUp.value.includes('all') ? 'all' : selectedFollowUp.value.join(',')
}

function getDurationDisplayText(): string {
  if (filters.duration === 'all') return ''
  const option = durationOptions.value.find((o) => o.value === filters.duration)
  return option?.label || ''
}

function getSeasonsDisplayText(): string {
  if (selectedSeasons.value.length === 0) return ''
  if (selectedSeasons.value.includes('all')) return 'All'
  const selectedLabels = selectedSeasons.value
    .map((value) => {
      const option = seasonsOptions.value.find((o) => o.value === value)
      return option?.label || ''
    })
    .filter(Boolean)
  return selectedLabels.join(', ')
}

function toggleSeasons(value: string): void {
  if (value === 'all') {
    if (selectedSeasons.value.includes('all')) {
      selectedSeasons.value = []
    } else {
      // 选择All时，同时选中所有其他选项
      selectedSeasons.value = seasonsOptions.value.map((o) => o.value)
    }
  } else {
    // 取消All的选中状态
    selectedSeasons.value = selectedSeasons.value.filter((v) => v !== 'all')
    const index = selectedSeasons.value.indexOf(value)
    if (index > -1) {
      selectedSeasons.value.splice(index, 1)
    } else {
      selectedSeasons.value.push(value)
    }
  }
  filters.seasons = selectedSeasons.value.includes('all') ? 'all' : selectedSeasons.value.join(',')
}

function toggleDropdown(field: keyof DropdownState): void {
  // 先关闭所有其他下拉框
  closeAllDropdowns()
  // 然后切换当前下拉框的状态
  dropdownOpen[field] = !dropdownOpen[field]
}

function selectOption(field: keyof typeof filters, value: string): void {
  filters[field] = value
  dropdownOpen[field as keyof DropdownState] = false
}

function closeAllDropdowns(): void {
  dropdownOpen.type = false
  dropdownOpen.section = false
  dropdownOpen.followUp = false
  dropdownOpen.duration = false
  dropdownOpen.seasons = false
}

function getIconUrl(icon: string): string {
  if (!icon) return ''
  if (icon.startsWith('http://') || icon.startsWith('https://')) return icon
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://hm.back.escroud.com'
  return icon.startsWith('/') ? `${baseUrl}${icon}` : `${baseUrl}/${icon}`
}

function mapBackendToFrontend(item: BackendChecklistItem): ChecklistItem {
  const priceText = item.follow_up_text || ''
  const icon = item.icon_url ? item.icon_url : ''
  return {
    id: String(item.id),
    itemId: item.item_id?.trim() || String(item.id),
    icon,
    title: item.item_name,
    duration: item.duration_text,
    isRoutine: item.type === 'routine',
    isBaseline: item.type === 'baseline',
    seasons: formatSeasonDisplayLabels(item.seasons),
    section: item.section,
    price: priceText,
    statusText: item.status_text || (item.status === 1 ? 'Active' : 'Inactive'),
    values: item.values,
  }
}

async function fetchChecklistItems(): Promise<void> {
  loading.value = true
  try {
    const params: ChecklistListParams = {
      page: currentPage.value,
    }
    if (filters.id) params.keyword = filters.id
    if (filters.type !== 'all') params.type = filters.type
    if (filters.section !== 'all') params.section = filters.section
    if (filters.followUp !== 'no_limit') params.follow_up = filters.followUp
    if (filters.duration !== 'all') params.duration_minutes = parseInt(filters.duration)
    if (filters.seasons !== 'all') params.seasons = [filters.seasons]
    const raw = await getChecklistList(params)
    const parsed = parseChecklistResponse(raw)
    if (parsed) {
      // 按创建时间倒序排列，最新创建的在最前面
      const sortedList = parsed.list.sort((a, b) => {
        return new Date(b.create_time).getTime() - new Date(a.create_time).getTime()
      })
      checklistItems.value = sortedList.map(mapBackendToFrontend)
      totalItems.value = parsed.total
      totalPages.value = parsed.totalPages
    }
  } catch (error) {
    console.error('Failed to fetch checklist items:', error)
  } finally {
    listLoaded.value = true
    loading.value = false
  }
}

/** 首屏固定展示空状态 INITIAL_EMPTY_MIN_MS，再进入 loading 请求 */
function startInitialLoadSequence(): void {
  if (initialFetchTimer) {
    clearTimeout(initialFetchTimer)
    initialFetchTimer = null
  }
  isInitialEmptyPhase.value = true
  loading.value = false
  listLoaded.value = false
  initialFetchTimer = setTimeout(() => {
    initialFetchTimer = null
    isInitialEmptyPhase.value = false
    void fetchChecklistItems()
  }, INITIAL_EMPTY_MIN_MS)
}

async function fetchFilterOptions(): Promise<void> {
  typeOptions.value = [
    { value: 'routine', label: 'Routine' },
    { value: 'baseline', label: 'Baseline' },
  ]
  sectionOptions.value = [
    { value: 'indoor', label: 'Indoor' },
    { value: 'roofing', label: 'Roofing' },
    { value: 'exterior', label: 'Exterior' },
    { value: 'structure', label: 'Structure' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'heating', label: 'Heating' },
    { value: 'cooling', label: 'Cooling' },
    { value: 'insulation', label: 'Insulation' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'interior', label: 'Interior' },
  ]
  followUpOptions.value = [
    { value: 'all', label: 'All' },
    { value: 'essential_care', label: 'Essential Care' },
    { value: 'enhanced_care', label: 'Enhanced Care' },
    { value: 'premium_care', label: 'Premium Care' },
  ]
  durationOptions.value = [
    { value: '5', label: '5 mins' },
    { value: '10', label: '10 mins' },
    { value: '15', label: '15 mins' },
    { value: '20', label: '20 mins' },
    { value: '25', label: '25 mins' },
    { value: '30', label: '30 mins' },
    { value: '60', label: '1 Hour' },
    { value: '90', label: '1.5 Hours' },
    { value: '120', label: '2.0 Hours' },
    { value: '150', label: '2.5 Hours' },
    { value: '180', label: '3.0 Hours' },
  ]
  seasonsOptions.value = [
    { value: 'all', label: 'All' },
    { value: 'spring', label: 'Spring' },
    { value: 'summer', label: 'Summer' },
    { value: 'autumn', label: 'Fall' },
    { value: 'winter', label: 'Winter' },
  ]
}

function handleSearch(): void {
  currentPage.value = 1
  closeAllDropdowns()
  void fetchChecklistItems()
}
function handleClear(): void {
  filters.id = ''
  filters.type = 'all'
  filters.section = 'all'
  filters.followUp = ''
  selectedFollowUp.value = []
  filters.duration = 'all'
  filters.seasons = ''
  selectedSeasons.value = []
  currentPage.value = 1
  closeAllDropdowns()
  void fetchChecklistItems()
}
function closeAllModals(): void {
  showNewModal.value = false
  showEditModal.value = false
  showSetValueModal.value = false
  showStatusModal.value = false
  showDeleteModal.value = false
  showDetailModal.value = false
}

function handleNew(): void {
  closeAllModals()
  showNewModal.value = true
}
function handleViewDetails(item: ChecklistItem): void {
  closeAllModals()
  currentItem.value = item
  showDetailModal.value = true
}
function handleDetailUpdated(itemId: string): void {
  void updateSingleItem(itemId)
}

async function handleDetailDeleted(): Promise<void> {
  showDetailModal.value = false
  await fetchChecklistItems()
  setTimeout(() => {
    handleToast({ type: 'success', message: 'Successfully deleted' })
  }, 300)
}
function handleEdit(item: ChecklistItem): void {
  closeAllModals()
  currentItem.value = item
  showEditModal.value = true
}
function handleSetValue(item: ChecklistItem): void {
  closeAllModals()
  currentItem.value = item
  showSetValueModal.value = true
}
function handleEditSaved(): void {
  if (currentItem.value) void updateSingleItem(currentItem.value.id)
}
function handleEditClose(): void {
  showEditModal.value = false
  currentItem.value = null
}
function handleSetValueSaved(): void {
  if (currentItem.value) void updateSingleItem(currentItem.value.id)
}
function handleSetValueClose(): void {
  closeAllModals()
  currentItem.value = null
}
function handleStatusSaved(): void {
  if (currentItem.value) void updateSingleItem(currentItem.value.id)
}
function handleStatusClose(): void {
  showStatusModal.value = false
  currentItem.value = null
}
function handleToggleStatus(item: ChecklistItem): void {
  closeAllModals()
  currentItem.value = item
  statusAction.value = item.statusText === 'Active' ? 'deactivate' : 'reactivate'
  showStatusModal.value = true
}
function handleDelete(item: ChecklistItem): void {
  closeAllModals()
  currentItem.value = item
  showDeleteModal.value = true
}
function handleDeleteClose(): void {
  showDeleteModal.value = false
  currentItem.value = null
}
async function handleDeleteConfirmed(success: boolean): Promise<void> {
  await fetchChecklistItems()
  if (success) {
    setTimeout(() => {
      handleToast({ type: 'success', message: 'Successfully deleted' })
    }, 300)
  }
}
async function updateSingleItem(itemId: string): Promise<void> {
  try {
    const response = await getChecklistDetail({ id: parseInt(itemId) })
    if (response.code === 200 && response.data) {
      const backendData = response.data
      const updatedItem: ChecklistItem = {
        id: String(backendData.id),
        itemId: backendData.item_id?.trim() || String(backendData.id),
        icon: backendData.icon_url || '',
        title: backendData.item_name,
        duration: backendData.duration_text,
        isRoutine: backendData.type === 'routine',
        isBaseline: backendData.type === 'baseline',
        seasons: backendData.seasons || [],
        section: backendData.section,
        price: backendData.follow_up_text || '',
        statusText: backendData.status_text || (backendData.status === 1 ? 'Active' : 'Inactive'),
      }
      const index = checklistItems.value.findIndex((item) => item.id === itemId)
      if (index !== -1) checklistItems.value[index] = updatedItem
    }
  } catch (error) {
    console.error('Failed to update single item:', error)
    void fetchChecklistItems()
  }
}
function handlePageChange(page: number): void {
  currentPage.value = page
  void fetchChecklistItems()
}
function handleNewSaved(): void {
  showNewModal.value = false
  handleToast({ type: 'success', message: 'Successfully saved' })
  currentPage.value = 1
  void fetchChecklistItems()
}
onMounted(() => {
  startInitialLoadSequence()
  void fetchFilterOptions()
  document.addEventListener('click', closeAllDropdowns)
})
onActivated(() => {
  if (!listLoaded.value) return
  void fetchChecklistItems()
})
onUnmounted(() => {
  if (initialFetchTimer) {
    clearTimeout(initialFetchTimer)
    initialFetchTimer = null
  }
  document.removeEventListener('click', closeAllDropdowns)
})
</script>
<style scoped>
.checklist-container {
  width: 100%;
  padding: 0;
  height: auto;
  overflow: visible;
}
.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin-top: -10px;
  margin-left: -10px;
  margin-bottom: 16px;
}

/* ==========筛选区域CSS（自适应修改版）========== */
.filter-section {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  margin-left: -10px;
  margin-right: -8px;
  box-sizing: border-box;
  overflow: visible;
}

/* ✅ 核心修改：自动响应式网格，最小宽度220px保证输入体验 */
.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  align-items: center;
  width: 100%;
}

.filter-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  height: 32px; /* 高度完全不变 */
  padding: 0 12px;
  box-sizing: border-box;
  width: 101%;
  transition: border-color 0.15s ease;
}

.filter-box:focus-within,
.filter-box.is-open {
  border-color: #666666;
}

.filter-actions-inline {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  grid-column: auto / -1;
}

.filter-label-inline {
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
}

.filter-input-inline {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #000000;
  background: transparent;
  min-width: 0;
}

.filter-input-inline::placeholder {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.filter-select-inline {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 400;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  background: transparent;
  cursor: pointer;
  min-width: 0;
}

.filter-select-inline::placeholder {
  color: #cccccc;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.select-arrow-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.filter-box.is-open .select-arrow-icon {
  transform: rotate(180deg);
}

.dropdown-panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  padding: 4px 0;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-option {
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #000000;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.dropdown-option:hover {
  background: #f5f5f5;
}

.dropdown-panel .dropdown-option.active span {
  font-weight: 600 !important;
}

.checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  cursor: pointer;
}

.checkbox.checked {
  background-color: #000000;
  border-color: #000000;
}

.btn-search {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #000000;
  color: #fff;
  border: none;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-search:hover {
  background-color: #484848;
}

.btn-clear {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #b0b0b0;
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-clear:hover {
  background-color: #cccccc;
}

/* ==========下方列表、header、空状态样式完全不变========== */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.count-info {
  font-size: 16px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  min-height: 24px;
}

.count-info--pending {
  visibility: hidden;
}

.list-body {
  position: relative;
}
.btn-new {
  background: #00aeef;
  color: #fff;
  width: 80px;
  height: 32px;
  border-radius: 16px;
  border: none;
  font-size: 14px;
  font-weight: 700;
  transform: translateX(8px);
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
}
.btn-new:hover {
  background: #2a5d8f;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

.list-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: auto;
  overflow: visible;
}
.list-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  overflow: hidden;
  margin-left: -8px;
  margin-right: -10px;
}
.card-header {
  height: 32px;
  padding: 0 16px;
  background: #f7f7f7;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}
.header-id {
  color: #999999;
}
.header-id-value {
  color: #000000;
  margin-left: 4px;
}
.card-body {
  display: grid;
  grid-template-columns: 689fr 160fr 160fr 160fr;
  min-height: 136px;
  height: auto;
}
.card-body > div {
  padding: 16px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
.card-body > div:nth-child(2),
.card-body > div:nth-child(3) {
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.card-body > div:last-child {
  border-right: none;
}

.column-main {
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  min-width: 0;
}
.item-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.item-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.placeholder-icon {
  width: 24px;
  height: 24px;
  opacity: 0.3;
}
.item-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
}
.item-title {
  word-break: break-word;
}
.item-duration {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}
.column-section {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  display: grid;
  place-items: center;
}

.column-status {
  display: grid;
  place-items: center;
  gap: 6px;
}
.status-badge {
  font-size: 14px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}
/* stylelint-disable-next-line no-unused-selectors */
.status-badge.active {
  color: #00aeef;
}
.status-badge.inactive {
  color: #e54848;
}
.view-details {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}


.column-actions {
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
}
.action-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.pagination-wrapper {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  margin-right: -10px;
}

/* ========== 你指定的统一滚动条样式 ========== */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* ========== 响应式代码（仅保留1套） ========== */
@media (max-width: 1200px) {
  .card-body {
    grid-template-columns: 1fr 1fr;
  }
  .card-body > div {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
}
@media (max-width: 576px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .filter-actions-inline {
    grid-column: 1/-1;
    justify-content: flex-end;
  }
  .card-body {
    grid-template-columns: 1fr;
  }
}
</style>
